import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, ArrowRight, X, Truck, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  confirmed: { bg: 'bg-[#10B981]/15', t: 'text-[#10B981]', l: 'Confirmed' },
  awaiting_payment: { bg: 'bg-[#F59E0B]/15', t: 'text-[#F59E0B]', l: 'Awaiting payment' },
  in_progress: { bg: 'bg-[#3B82F6]/15', t: 'text-[#3B82F6]', l: 'In progress' },
  completed: { bg: 'bg-[#0F172A]/10', t: 'text-[#0F172A]', l: 'Completed' },
  cancelled: { bg: 'bg-[#EF4444]/15', t: 'text-[#EF4444]', l: 'Cancelled' },
  pending_callback: { bg: 'bg-[#F59E0B]/15', t: 'text-[#F59E0B]', l: 'Pending callback' },
};

const UserBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [moverLeads, setMoverLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [b, m] = await Promise.all([
        fetch(`${API}/services/bookings/me`, { headers }).then(r => r.json()),
        fetch(`${API}/movers/leads/me`, { headers }).then(r => r.json()),
      ]);
      setBookings(b.bookings || []);
      setMoverLeads(m.leads || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (booking_id) => {
    setCancelling(booking_id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/services/bookings/${booking_id}/cancel`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Cancel failed');
      toast.success('Booking cancelled');
      load();
    } catch (e) { toast.error(e.message); }
    finally { setCancelling(null); }
  };

  if (loading) return <div className="bg-white p-8 rounded-sm border text-center text-muted-foreground">Loading your bookings...</div>;

  const empty = bookings.length === 0 && moverLeads.length === 0;

  return (
    <div className="space-y-8" data-testid="user-bookings">
      {empty && (
        <div className="bg-white p-10 rounded-sm border border-border/50 text-center">
          <Sparkles className="h-10 w-10 text-[#D4AF37] mx-auto mb-3" />
          <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-2">No bookings yet</h3>
          <p className="text-sm text-muted-foreground mb-6">Book a home service or get a movers quote.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/services"><Button className="bg-[#0F172A] text-white">Browse Services</Button></Link>
            <Link to="/packers-movers"><Button variant="outline">Packers & Movers</Button></Link>
          </div>
        </div>
      )}

      {bookings.length > 0 && (
        <div>
          <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#D4AF37]" /> Service Bookings ({bookings.length})
          </h3>
          <div className="space-y-4">
            {bookings.map((b) => {
              const st = STATUS_PILL[b.status] || STATUS_PILL.confirmed;
              return (
                <div key={b.booking_id} className="bg-white rounded-sm border border-border/50 p-5" data-testid={`booking-${b.booking_id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground tracking-wider">Booking ID</p>
                      <p className="font-mono font-semibold text-[#0F172A]">{b.booking_id}</p>
                    </div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.t}`}>{st.l}</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-sm">
                    <div className="flex items-start gap-2"><Calendar className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" /> <span>{new Date(b.slot_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span></div>
                    <div className="flex items-start gap-2"><Clock className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" /> <span>{b.slot_time}</span></div>
                    <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" /> <span className="truncate">{b.city} - {b.pincode}</span></div>
                  </div>

                  <div className="bg-[#F9F9F7] p-3 rounded-sm mb-4">
                    <ul className="text-sm space-y-1">
                      {b.items.map((i) => (
                        <li key={i.service_id} className="flex justify-between">
                          <span className="text-[#0F172A]">{i.name} × {i.quantity}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Payment</p>
                      <p className="text-sm font-semibold text-[#0F172A]">Cash on Service · quote shared on call</p>
                    </div>
                    {b.status !== 'cancelled' && b.status !== 'completed' && (
                      <Button variant="outline" size="sm" disabled={cancelling === b.booking_id} onClick={() => cancel(b.booking_id)} data-testid={`cancel-${b.booking_id}`}>
                        <X className="h-4 w-4 mr-1" /> {cancelling === b.booking_id ? 'Cancelling...' : 'Cancel Booking'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {moverLeads.length > 0 && (
        <div>
          <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Truck className="h-5 w-5 text-[#D4AF37]" /> Packers &amp; Movers Quotes ({moverLeads.length})
          </h3>
          <div className="space-y-4">
            {moverLeads.map((l) => {
              const st = STATUS_PILL[l.status] || STATUS_PILL.pending_callback;
              return (
                <div key={l.lead_id} className="bg-white rounded-sm border border-border/50 p-5" data-testid={`mover-lead-${l.lead_id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Quote ID</p>
                      <p className="font-mono font-semibold text-[#0F172A]">{l.lead_id}</p>
                    </div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.t}`}>{st.l}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                    <div><p className="text-xs text-muted-foreground">Route</p><p className="font-semibold text-[#0F172A]">{l.from_city} → {l.to_city}</p></div>
                    <div><p className="text-xs text-muted-foreground">Size · Tier</p><p className="font-semibold text-[#0F172A] uppercase">{l.size_id} · {l.tier_id}</p></div>
                    <div><p className="text-xs text-muted-foreground">Move Date</p><p className="font-semibold text-[#0F172A]">{l.move_date}</p></div>
                    <div><p className="text-xs text-muted-foreground">Estimate</p><p className="font-semibold text-[#0F172A]">Quote shared on call</p></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-center pt-4">
        <Link to="/services">
          <Button className="bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold px-6 py-5">
            Book another service <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default UserBookings;
